import { useState, useEffect, useCallback } from "react";
import {
  carregarAtletas, carregarModalidadesDoNucleo, carregarDispensasDoMes,
  carregarDiasTreinoFisico, carregarPresencaFisica, salvarPresencaFisica,
  carregarNomesAtuais, carregarPresencasDoMes,
} from "../firebase";
import { calcularFrequencia, normalizarModalidade } from "../calculo";
import { CINZA_ESC } from "../tema";
import {
  Card, Sel, StatusBox, Carregando,
  Marca, LegendaDePresenca, CabecalhoDoDia, corDoPercentual,
  estilosDaGrade, NomeNaGrade,
} from "../componentes";
import useIsMobile from "../useIsMobile";
import GradePresencaFisicaDupla from "../componentes/GradePresencaFisicaDupla";

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const diaLabel = iso => {
  const dt = new Date(iso + "T12:00:00");
  const semana = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"][dt.getDay()];
  return { num: String(dt.getDate()).padStart(2, "0"), semana };
};

// PRESENÇA NO TREINO FÍSICO — grade dias × atletas, no mesmo visual da
// caderneta dos técnicos. Informativa (não afeta a frequência oficial).
// Preparador/auxiliar marcam (podeMarcar=true); coordenação só visualiza.
// modalidadeFixa: quando vem preenchida (técnico/auxiliar), a seção trava
// naquela modalidade e não mostra o seletor. O filtro na tela é
// conveniência; quem garante o isolamento é a regra do Firestore.
export default function SecaoPresencaFisica({ userNucleo, podeMarcar = false, modalidadeFixa = "" }) {
  const estreito = useIsMobile();
  const GRADE = estilosDaGrade(estreito);
  const hoje = new Date();
  const [mes, setMes] = useState(String(hoje.getMonth()));
  const [ano, setAno] = useState(String(hoje.getFullYear()));
  const [modalidade, setModalidade] = useState(modalidadeFixa || "");
  const [modalidades, setModalidades] = useState([]);
  const [atletas, setAtletas] = useState([]);
  const [diasConfig, setDiasConfig] = useState({});
  const [presencas, setPresencas] = useState({});
  const [calcTecnico, setCalcTecnico] = useState(null); // só no modo coordenador
  const [carregando, setCarregando] = useState(true);
  const [status, setStatus] = useState(null);

  const anoAtual = hoje.getFullYear();
  const anos = [anoAtual - 1, anoAtual, anoAtual + 1].map(String);
  const mesPrefixo = `${ano}-${String(parseInt(mes) + 1).padStart(2, "0")}`;

  useEffect(() => { setModalidade(modalidadeFixa || ""); }, [userNucleo, modalidadeFixa]);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    setStatus(null);
    try {
      const [mods, atls, cfg, pres] = await Promise.all([
        carregarModalidadesDoNucleo(userNucleo),
        carregarAtletas(userNucleo),
        carregarDiasTreinoFisico(userNucleo),
        carregarPresencaFisica(userNucleo, mesPrefixo),
      ]);
      setModalidades(mods || []);
      setAtletas((atls || []).filter(a => a.grupo === "ATLETA"));
      setDiasConfig(cfg || {});
      setPresencas(pres || {});

      // Só o coordenador (modo leitura) precisa da frequência TÉCNICA para
      // montar a visão de duas linhas. O preparador não carrega isso.
      if (!podeMarcar) {
        const mesNum = parseInt(mes), anoNum = parseInt(ano);
        // O banco filtra o mês, e não o navegador.
        const registrosDoMes = await carregarPresencasDoMes(userNucleo, mesNum, anoNum);
        const dispensasDoMes = await carregarDispensasDoMes(userNucleo, mesNum, anoNum);
        // nomesAtuais traz o status ativo/inativo de cada cadastro — sem
        // ele o cálculo não sabe quem saiu, e a grade não conseguiria
        // distinguir o inativo que ainda tem registro no mês.
        const nomesAtuais = await carregarNomesAtuais(userNucleo);
        const resultado = calcularFrequencia({ registros: registrosDoMes, dispensas: dispensasDoMes, mes: mesNum, ano: anoNum, nomesAtuais });
        setCalcTecnico(resultado);
      } else {
        setCalcTecnico(null);
      }
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Erro ao carregar: " + (e.message || "") });
    } finally {
      setCarregando(false);
    }
  }, [userNucleo, mesPrefixo, podeMarcar, mes, ano]);

  useEffect(() => { recarregar(); }, [recarregar]);

  function diasDeTreinoDoMes() {
    if (!modalidade) return [];
    const modLower = normalizarModalidade(modalidade).toLowerCase();
    const diasSemana = diasConfig[modLower] || [];
    if (diasSemana.length === 0) return [];
    const y = parseInt(ano), m = parseInt(mes);
    const ultimo = new Date(y, m + 1, 0).getDate();
    const hojeISO = new Date().toISOString().split("T")[0];
    const dias = [];
    for (let d = 1; d <= ultimo; d++) {
      const dt = new Date(y, m, d);
      const iso = dt.toISOString().split("T")[0];
      if (diasSemana.includes(dt.getDay()) && iso <= hojeISO) dias.push(iso);
    }
    return dias;
  }

  // Pelo NOME — a mesma chave do documento gravado e da leitura. Usar o
  // e-mail aqui divergiria dos registros existentes, que foram gravados
  // quando a lista de atletas ainda não trazia e-mail.
  function chaveAtleta(a) {
    return (a.nome || "").toLowerCase().replace(/[^a-z0-9]/g, "_");
  }

  async function alternar(atleta, data) {
    if (!podeMarcar) return;
    // Atleta desligado não recebe marcação nova. Ele pode estar visível
    // por ter registro no mês, mas não está mais disponível para registro.
    if (atleta.ativo === false) {
      setStatus({ tipo: "erro", msg: `${atleta.nome} está inativo no cadastro e não recebe novas marcações.` });
      return;
    }
    const chave = `${chaveAtleta(atleta)}__${data}`;
    const atual = presencas[chave];
    const proximo = atual === "presente" ? "falta" : atual === "falta" ? null : "presente";
    setPresencas(p => ({ ...p, [chave]: proximo }));
    try {
      await salvarPresencaFisica({
        nucleo: userNucleo, modalidade,
        atletaUid: atleta.uid || null,
        atletaNome: atleta.nome, atletaEmail: atleta.email || "",
        data, status: proximo,
      });
    } catch (e) {
      setPresencas(p => ({ ...p, [chave]: atual }));
      setStatus({ tipo: "erro", msg: "Erro ao salvar: " + (e.message || "") });
    }
  }

  const dias = diasDeTreinoDoMes();
  const modLower = modalidade ? normalizarModalidade(modalidade).toLowerCase() : "";
  const temConfig = modLower && (diasConfig[modLower] || []).length > 0;
  // Quem tem registro de presença no mês, para não sumir da grade quem
  // foi desligado no meio do período — a presença dele aconteceu.
  const nomesComRegistro = new Set(
    ((calcTecnico && calcTecnico.membros) || []).map(m => (m.nome || "").toLowerCase())
  );

  // Atleta INATIVO só aparece se tiver registro no mês. Inativo sem
  // registro nenhum não entra na grade: ele não faz mais parte do
  // programa e não há nada dele para mostrar naquele período.
  const atletasDaMod = modalidade
    ? atletas.filter(a =>
        normalizarModalidade(a.modalidade).toLowerCase() === modLower
        && (a.ativo !== false || nomesComRegistro.has((a.nome || "").toLowerCase()))
      )
    : [];

  function statusDoDia(atleta, iso) {
    return presencas[`${chaveAtleta(atleta)}__${iso}`] || null;
  }
  function pctDoAtleta(atleta) {
    if (dias.length === 0) return 0;
    const presentes = dias.filter(iso => statusDoDia(atleta, iso) === "presente").length;
    return presentes / dias.length;
  }

  // Ordem alfabética FIXA pelo nome — diferente dos outros painéis (que
  // ordenam por %). Como aqui o preparador marca presença/falta na hora, uma
  // ordem por % faria o nome "pular" de posição a cada clique. Fixa evita isso.
  const ordenados = [...atletasDaMod].sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "pt-BR"));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Presença Treino Físico</div>
        <div style={{ fontSize: 13, color: CINZA_ESC, lineHeight: 1.6 }}>
          {podeMarcar
            ? "Toque no quadradinho de cada dia para marcar presença (verde), falta (vermelho) ou deixar em branco. Este acompanhamento é informativo e não altera a frequência oficial (técnica) do atleta."
            : "Acompanhamento da presença no treino físico, registrada pelo preparador. É informativo e não altera a frequência oficial dos atletas."}
        </div>
      </Card>

      <Card style={{ display: "grid", gridTemplateColumns: modalidadeFixa ? "1fr 1fr" : "1fr 1fr 1fr", gap: 12 }}>
        <Sel label="Mês" value={mes} onChange={setMes}
          options={MESES.map((m, i) => ({ value: String(i), label: m }))} />
        <Sel label="Ano" value={ano} onChange={setAno} options={anos} />
        {!modalidadeFixa && (
          <Sel label="Modalidade" value={modalidade} onChange={setModalidade}
            options={[{ value: "", label: "— selecione —" }, ...modalidades.map(m => ({ value: m, label: m }))]} />
        )}
      </Card>

      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

      {carregando ? (
        <Card><Carregando linhas={6} /></Card>
      ) : !modalidade ? (
        <Card style={{ textAlign: "center", color: CINZA_ESC, padding: "28px 24px" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🏋️</div>
          <div style={{ fontSize: 14 }}>Selecione uma modalidade para ver a presença do treino físico.</div>
        </Card>
      ) : !podeMarcar ? (
        // ── MODO COORDENADOR: visão de DUAS LINHAS (técnico + físico) ──
        (() => {
          // diasVisiveis, e não diasTreino: os dois são quase iguais, e a
          // diferença é justamente o dia de HOJE. diasTreino é o denominador
          // do percentual, e o dia corrente só entra nele às 18h30, quando o
          // treino fecha. Usá-lo como coluna escondia da grade tudo que
          // acontecia durante a tarde: o coordenador e o técnico só viam a
          // presença do dia depois das 18h30, mesmo já estando gravada.
          const diasTec = (calcTecnico?.diasVisiveis) || (calcTecnico?.diasTreino) || [];
          const membrosTec = (calcTecnico?.membros) || [];
          const atletasDupla = atletasDaMod.map(a => {
            const modLower2 = normalizarModalidade(a.modalidade).toLowerCase();
            // Casa o atleta com o membro calculado (por nome normalizado).
            const mt = membrosTec.find(m =>
              (m.nome || "").toLowerCase() === (a.nome || "").toLowerCase() &&
              normalizarModalidade(m.modalidade).toLowerCase() === modLower2
            );
            const mapaTecnico = {};
            (mt?.mapaDiario || []).forEach(d => { mapaTecnico[d.dia] = d; });
            return { nome: a.nome, modalidade: modLower2, mapaTecnico, pctTec: mt?.pctTotal || 0 };
          });
          if (diasTec.length === 0) {
            return (
              <Card style={{ textAlign: "center", color: CINZA_ESC, padding: "28px 24px" }}>
                <div style={{ fontSize: 14 }}>Nenhum dia de treino técnico em {MESES[parseInt(mes)]}/{ano} até hoje.</div>
              </Card>
            );
          }
          if (atletasDupla.length === 0) {
            return (
              <Card style={{ textAlign: "center", color: CINZA_ESC, padding: "28px 24px" }}>
                <div style={{ fontSize: 14 }}>Nenhum atleta de {modalidade} neste núcleo.</div>
              </Card>
            );
          }
          return (
            <GradePresencaFisicaDupla
              atletas={atletasDupla}
              diasTreino={diasTec}
              diasFisicoSemana={diasConfig}
              presencaFisica={presencas}
              chaveDe={chaveAtleta}
            />
          );
        })()
      ) : !temConfig ? (
        <Card style={{ textAlign: "center", color: CINZA_ESC, padding: "28px 24px" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
          <div style={{ fontSize: 14 }}>
            {modalidade} ainda não tem dias de treino físico configurados.
            {podeMarcar ? " Configure na aba \"Dias de treino\"." : " O preparador precisa configurar os dias de treino."}
          </div>
        </Card>
      ) : dias.length === 0 ? (
        <Card style={{ textAlign: "center", color: CINZA_ESC, padding: "28px 24px" }}>
          <div style={{ fontSize: 14 }}>Nenhum dia de treino físico de {modalidade} até hoje em {MESES[parseInt(mes)]}/{ano}.</div>
        </Card>
      ) : atletasDaMod.length === 0 ? (
        <Card style={{ textAlign: "center", color: CINZA_ESC, padding: "28px 24px" }}>
          <div style={{ fontSize: 14 }}>Nenhum atleta de {modalidade} neste núcleo.</div>
        </Card>
      ) : (
        <div>
          <div style={GRADE.caixa}>
            <table style={GRADE.tabela}>
              <thead>
                <tr style={GRADE.cabecalho}>
                  <th style={GRADE.thNome}>Atleta</th>
                  {dias.map(iso => {
                    const { num, semana } = diaLabel(iso);
                    return (
                      <th key={iso} style={GRADE.thDia}>
                        <CabecalhoDoDia num={num} semana={semana} />
                      </th>
                    );
                  })}
                  <th style={GRADE.thPct}>%</th>
                </tr>
              </thead>
              <tbody>
                {ordenados.map(a => {
                  const pct = pctDoAtleta(a);
                  return (
                    <tr key={a.nome} style={GRADE.linha}>
                      <td style={GRADE.tdNome}>
                        <NomeNaGrade nome={a.nome} estreito={estreito} />
                      </td>
                      {dias.map(iso => {
                        const st = statusDoDia(a, iso);
                        const tipo = st === "presente" ? "presente" : st === "falta" ? "falta" : "naoMarcado";
                        const legenda = st === "presente" ? "Presente" : st === "falta" ? "Falta" : "Não marcado";
                        return (
                          <td key={iso} style={GRADE.tdDia}>
                            <Marca tipo={tipo} clicavel={podeMarcar} estreito={estreito}
                              onClick={() => alternar(a, iso)}
                              titulo={`${diaLabel(iso).num} — ${legenda}`} />
                          </td>
                        );
                      })}
                      <td style={{ ...GRADE.tdPct, color: corDoPercentual(pct) }}>{Math.round(pct * 100)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <LegendaDePresenca tipos={[
            { tipo: "presente" },
            { tipo: "falta" },
            { tipo: "naoMarcado" },
          ]} />
          {podeMarcar && (
            <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 8 }}>
              Toque no quadradinho para alternar: presente → falta → em branco.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
